import { useEffect, useState } from 'react';

// Third-party
import clsx from 'clsx';
import dayjs from 'dayjs';

// Hooks
import useReplies from '@/utils/hooks/swr/useReplies';
import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';

//Utils
import { deleteApi, post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';
import useToggle from '@/utils/hooks/utils/useToggle';

import PropTypes from 'prop-types';

//Endpoints
import { COMMENT_DELETE, COMMENT_MARK_AS_READ } from '@/lib/routes/apiEndpoints';

// Components
import CommentLoading from '@/components/base/Comment/CommentLoading';
import ReplyComment from '@/components/composite/Comments/ReplyComment';
import { ReplyFooterButtons } from '@/components/composite/Comments/ReplyFooterButtons';
import CommentDeleteModal from '@/components/composite/Modals/CommentDeleteModal';

const deleteReplyStateInitial = {
    replyId: null,
    postId: null,
    isOpenModal: false,
    isLoading: false,
    isError: false
};

const RepliesList = ({
    comment,
    socialProfile,
    className,
    activeFormReply,
    setActiveFormReply,
    replyToAdd,
    setReplyToAdd,
    mutateComments,
    markRepliesAsRead,
    setMarkAllAsRead,
    variant = 'default'
}) => {
    const [deleteReplyState, setDeleteReplyState] = useState(deleteReplyStateInitial);
    const { isOpen: isThreadOpen, toggle: toggleThread } = useToggle({ initial: true });
    const { t } = useTranslation('commons');
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const {
        replies: rawReplies,
        loadMoreReplies,
        isLoadingInitialData,
        isLoadingMore,
        countReplies,
        mutateReplies,
        isLoadMore
    } = useReplies({
        activeSPID: socialProfile?.id,
        mediaId: comment?.media_id,
        commentId: comment?.id
    });

    const replies =
        variant === 'socialInbox'
            ? rawReplies.toSorted((a, b) => new Date(a.created) - new Date(b.created))
            : rawReplies;

    const deleteReply = async () => {
        setDeleteReplyState((prevState) => ({ ...prevState, isLoading: true, isError: false }));
        try {
            await deleteApi(
                injectEndpointUrlParams(COMMENT_DELETE, {
                    SPID: socialProfile.id,
                    mediaID: deleteReplyState.postId,
                    commentID: deleteReplyState.replyId
                })
            );
            mutateReplies(
                (prevState) =>
                    prevState.map((page) => ({
                        ...page,
                        data: page.data.filter((reply) => reply.id !== deleteReplyState.replyId)
                    })),
                false
            );
            mutateComments(
                (prevState) =>
                    prevState.map((page) => ({
                        ...page,
                        data: page.data.map((dataComment) => {
                            if (dataComment.id === comment.id) {
                                return {
                                    ...dataComment,
                                    replies_count: dataComment.replies_count - 1
                                };
                            }

                            return dataComment;
                        }),
                        random_picker_winners: Array.isArray(page?.random_picker_winners)
                            ? page?.random_picker_winners?.map((dataComment) => {
                                  if (dataComment.id === comment.id) {
                                      return {
                                          ...dataComment,
                                          replies_count: dataComment.replies_count - 1
                                      };
                                  }

                                  return dataComment;
                              })
                            : page?.random_picker_winners,
                        summary: {
                            ...page.summary,
                            total_count: page.summary.total_count - 1
                        }
                    })),
                false
            );
            setDeleteReplyState(deleteReplyStateInitial);
        } catch (e) {
            setDeleteReplyState((prevState) => ({ ...prevState, isLoading: false, isError: true }));
        }
    };

    const markAsRead = async (commentId, mediaId) => {
        try {
            await post(
                injectEndpointUrlParams(COMMENT_MARK_AS_READ, {
                    SPID: socialProfile?.id,
                    mediaID: mediaId,
                    commentID: commentId
                })
            );
            mutateReplies(
                (prevState) =>
                    prevState?.map((page) => ({
                        ...page,
                        data: page.data.map((reply) => {
                            if (reply.id === commentId) {
                                return {
                                    ...reply,
                                    is_read: true
                                };
                            }

                            return reply;
                        })
                    })),
                false
            );
            mutateComments(
                (prevState) =>
                    prevState.map((page) => ({
                        ...page,
                        summary: {
                            ...page.summary,
                            unread_count: 0
                        }
                    })),
                false
            );

            return {
                success: true
            };
        } catch (e) {
            return {
                success: false
            };
        }
    };

    useEffect(() => {
        if (replyToAdd && replyToAdd?.commentId === comment?.id) {
            mutateReplies(
                (prevState) =>
                    prevState?.map((page, index) =>
                        index === 0
                            ? {
                                  ...page,
                                  data: [replyToAdd.reply, ...page.data]
                              }
                            : page
                    ),
                false
            );
            mutateComments(
                (prevState) =>
                    prevState.map((page) => ({
                        ...page,
                        data: page.data.map((dataComment) => {
                            if (dataComment.id === comment.id) {
                                return {
                                    ...dataComment,
                                    replies_count: dataComment.replies_count + 1
                                };
                            }

                            return dataComment;
                        }),
                        random_picker_winners: Array.isArray(page?.random_picker_winners)
                            ? page?.random_picker_winners?.map((dataComment) => {
                                  if (dataComment.id === comment.id) {
                                      return {
                                          ...dataComment,
                                          replies_count: dataComment.replies_count + 1
                                      };
                                  }

                                  return dataComment;
                              })
                            : page?.random_picker_winners,
                        summary: {
                            ...page.summary,
                            total_count: page.summary.total_count + 1
                        }
                    })),
                false
            );
            setReplyToAdd(null);
        }
    }, [replyToAdd]);

    useEffect(() => {
        if (markRepliesAsRead) {
            mutateReplies(
                (prevState) =>
                    prevState.map((page) => ({
                        ...page,
                        data: page.data.map((reply) => ({
                            ...reply,
                            is_read: true
                        }))
                    })),
                false
            );
        }
        setMarkAllAsRead((prevState) => ({
            ...prevState,
            markRepliesAsRead: false
        }));
    }, [markRepliesAsRead]);

    const checkNewMessage = (messageDate) => {
        const fiveMinutesAgo = dayjs().subtract(5, 'minutes');
        return dayjs(messageDate).isAfter(fiveMinutesAgo);
    };

    const renderNewMessageDivider = (message, index) => {
        if (variant === 'default') return null;

        // Will only render the divider if it's the first new message in the list
        const isFirstNewMessage =
            replies.findIndex((msg) => checkNewMessage(msg.created)) === index;

        if (isFirstNewMessage && checkNewMessage(message.created)) {
            return (
                <div className="flex w-full items-center gap-2">
                    <span className="whitespace-nowrap text-xxs uppercase tracking-wider text-icoBlue dark:text-white">
                        {t('comment.new_messages')}
                    </span>
                    <div className="h-[1px] w-full bg-icoBlue-200 dark:bg-white"></div>
                </div>
            );
        }

        return null;
    };

    const renderReplies = () => {
        const commonProps = {
            activeFormReply,
            comment,
            markAsRead,
            setActiveFormReply,
            setDeleteReplyState,
            setReplyToAdd,
            socialProfile,
            user,
            variant,
            className: variant === 'default' ? 'p-2.5 pr-0' : ''
        };

        return replies.map((reply, index) => {
            // Skip rendering if it's not one of the first two replies and thread is not open
            if (index >= 2 && (!isThreadOpen || countReplies <= 2)) {
                return null;
            }

            const isFirstTwoReplies = index < 2;
            const replyProps = {
                ...commonProps,
                reply,
                relatedComments: isFirstTwoReplies ? [{ data: replies }] : undefined
            };

            return (
                <div
                    key={reply.id}
                    className={clsx('transition-all duration-150', {
                        'flex flex-col gap-3': isFirstTwoReplies,
                        'space-y-1': !isFirstTwoReplies
                    })}>
                    {renderNewMessageDivider(reply, index)}

                    <ReplyComment {...replyProps} />
                </div>
            );
        });
    };

    if (isLoadingInitialData && comment?.replies_count && comment?.replies_count > 0) {
        return (
            <div
                className={clsx(
                    className,
                    'relative mt-2.5 flex flex-col space-y-1 border-l pl-2.5 dark:border-icoDarkMode-beton'
                )}>
                {[
                    ...Array(
                        comment?.replies_count && comment?.replies_count > 2
                            ? 2
                            : comment?.replies_count
                    ).keys()
                ].map((index) => (
                    <CommentLoading key={index} className="p-2.5 pr-0" />
                ))}
            </div>
        );
    }

    if (replies?.length) {
        return (
            <div
                className={clsx(
                    className,
                    'relative flex flex-col border-l pl-2.5 dark:border-icoDarkMode-beton',
                    {
                        'mt-2.5 space-y-1': variant === 'default',
                        'mt-5 gap-5': variant === 'socialInbox'
                    }
                )}>
                {renderReplies()}

                <ReplyFooterButtons
                    isLoadMore={isLoadMore}
                    countReplies={countReplies}
                    isThreadOpen={isThreadOpen}
                    isLoadingMore={isLoadingMore}
                    isLoadingInitialData={isLoadingInitialData}
                    loadMoreReplies={loadMoreReplies}
                    toggleThread={toggleThread}
                    comment={comment}
                    className={variant === 'default' ? 'px-2.5' : undefined}
                />

                <CommentDeleteModal
                    isOpen={deleteReplyState.isOpenModal}
                    toggle={() => {
                        setDeleteReplyState((prevState) => ({
                            ...prevState,
                            isOpenModal: false
                        }));
                    }}
                    deleteComment={deleteReply}
                    isLoading={deleteReplyState.isLoading}
                    isError={deleteReplyState.isError}
                />
            </div>
        );
    }

    return null;
};

RepliesList.propTypes = {
    comment: PropTypes.object.isRequired,
    className: PropTypes.string,
    replyToAdd: PropTypes.object,
    socialProfile: PropTypes.object.isRequired,
    setReplyToAdd: PropTypes.func,
    activeFormReply: PropTypes.object,
    setActiveFormReply: PropTypes.func,
    variant: PropTypes.oneOf(['default', 'socialInbox']),
    setMarkAllAsRead: PropTypes.func,
    markRepliesAsRead: PropTypes.bool,
    mutateComments: PropTypes.func,
    mutateReplies: PropTypes.func
};

export default RepliesList;
